import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loader2, Mail, UserPlus, Shield, MapPin } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useRolesQuery, useInviteUserMutation } from '@/features/users/hooks'
import { FRANCHISE_REGIONS } from '@/features/users/utils/franchiseRegions'

interface InviteUserModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function InviteUserModal({ open, onOpenChange, onSuccess }: InviteUserModalProps) {
  const [email, setEmail] = useState('')
  const [roleId, setRoleId] = useState('')
  const [selectedFranchises, setSelectedFranchises] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const [step, setStep] = useState(1)
  const { success: toastSuccess, error: toastError } = useToast()

  // Fetch available roles
  const { data: roles, isLoading: rolesLoading } = useRolesQuery()

  // Send invitation mutation
  const inviteMutation = useInviteUserMutation({
    onSuccess: () => {
      toastSuccess('Invitation sent', 'The user will receive an email invitation.')
      // Reset form
      setEmail('')
      setRoleId('')
      setSelectedFranchises([])
      setMessage('')
      setStep(1)
      onSuccess()
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to send invitation'
      toastError('Failed to send invitation', message)
    }
  })

  const handleFranchiseToggle = (franchise: string, checked: boolean) => {
    if (checked) {
      setSelectedFranchises(prev => [...prev, franchise])
    } else {
      setSelectedFranchises(prev => prev.filter(f => f !== franchise))
    }
  }

  const selectedRole = roles?.find(r => r.id === roleId)
  const isAdminRole = selectedRole?.name === 'admin'

  const handleNext = () => {
    if (step === 1 && email && roleId) {
      setStep(2)
    }
  }

  const handleBack = () => {
    setStep(1)
  }

  const handleSubmit = () => {
    inviteMutation.mutate({
      email,
      roleId,
      franchiseRegions: selectedFranchises,
      message
    })
  }

  const canProceed = step === 1 ? (email && roleId) : true

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite New User
          </DialogTitle>
          <DialogDescription>
            Send an invitation to join the FixMyBrick Admin Dashboard
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="text-sm font-medium">User Details</span>
            </div>
            <div className={`h-px flex-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="text-sm font-medium">Permissions</span>
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {rolesLoading ? (
                      <div className="p-2 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </div>
                    ) : (
                      roles?.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            <div>
                              <div className="font-medium">{role.name}</div>
                              <div className="text-xs text-gray-500">{role.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {selectedRole && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      {selectedRole.name} Role
                    </CardTitle>
                    <CardDescription>{selectedRole.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={selectedRole.name === 'admin' ? 'destructive' : 'default'}>
                      Priority: {selectedRole.priority}
                    </Badge>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <Label>Franchise Access</Label>
                </div>

                {isAdminRole ? (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center text-gray-500">
                        <Shield className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                        <p className="font-medium">Admin users have access to all franchises</p>
                        <p className="text-sm">No franchise restrictions apply to admin roles</p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      Select which franchise locations this user can access. If none are selected, they will have access to all franchises.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {FRANCHISE_REGIONS.map((franchise) => (
                        <div key={franchise.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={franchise.value}
                            checked={selectedFranchises.includes(franchise.value)}
                            onCheckedChange={(checked) =>
                              handleFranchiseToggle(franchise.value, checked as boolean)
                            }
                          />
                          <Label htmlFor={franchise.value} className="text-sm font-normal">
                            {franchise.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {selectedFranchises.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-2">
                        {selectedFranchises.map((franchise) => {
                          const region = FRANCHISE_REGIONS.find(f => f.value === franchise)
                          return (
                            <Badge key={franchise} variant="outline">
                              {region?.label}
                            </Badge>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Welcome Message (Optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Add a personal message to the invitation..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Summary */}
              <Card className="bg-gray-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Invitation Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><strong>Email:</strong> {email}</div>
                  <div><strong>Role:</strong> {selectedRole?.name}</div>
                  <div>
                    <strong>Franchise Access:</strong> {
                      isAdminRole
                        ? 'All franchises (Admin)'
                        : selectedFranchises.length > 0
                          ? `${selectedFranchises.length} selected franchises`
                          : 'All franchises'
                    }
                  </div>
                  {message.trim() && (
                    <div><strong>Message:</strong> {message.length > 50 ? message.substring(0, 50) + '...' : message}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <div>
              {step === 2 && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {step === 1 ? (
                <Button onClick={handleNext} disabled={!canProceed}>
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={inviteMutation.isPending}
                >
                  {inviteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
